import { memo, useState } from 'react';
import { usePlugins, useReporterState } from '../hooks';
import { Portal } from 'common/ui/Portal';

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { DropdownIndicator, ToolbarButton } from 'common/ui/Toolbar';
import { Stack } from 'common/ui/Layout';
import Tooltip from 'common/ui/Tooltip';
import type { ToolbarConfigPluginID } from '../plugins/types';

type PortalToolbarButtonProps = {
  pluginID: ToolbarConfigPluginID;
  height?: string;
};

export const PortalToolbarButton = memo(function PortalToolbarButton({
  pluginID,
  height,
}: PortalToolbarButtonProps): React.ReactElement | null {
  const { navMountPoint } = useReporterState();
  const { getToolbarConfigs } = usePlugins();

  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context, placement } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware({ mainAxis: 5 }),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: { bottom: 15 },
        apply({ availableHeight, elements }) {
          const inner = elements.floating.firstElementChild as HTMLElement | null;
          if (inner != null) inner.style.maxHeight = `${Math.max(0, availableHeight)}px`;
        },
      }),
    ],
  });

  // The dropdown opens on mousedown; preventDefault keeps the editor selection
  // from collapsing when the toolbar button is pressed.
  const click = useClick(context, { event: 'mousedown' });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const instance = { hide: () => setOpen(false), unmount: () => setOpen(false) };

  // Avoid creating a full intermediate config list on each lookup.
  // We resolve only the requested plugin config and stop as soon as it is found.
  let activatedToolbarConfig = null;
  for (const createToolbarConfig of getToolbarConfigs()) {
    const config = createToolbarConfig();
    if (config.pluginID === pluginID) {
      activatedToolbarConfig = config;
      break;
    }
  }

  if (activatedToolbarConfig == null) return null;

  const { toolbarButton, component: Component, options } = activatedToolbarConfig;

  // TODO: @kcwijaya - figure out why merge fields button overflows bounds on hover
  return (
    <Portal mountPoint={navMountPoint} key={pluginID}>
      <Tooltip content={options?.tooltipText}>
        <ToolbarButton
          ref={refs.setReference}
          {...getReferenceProps({ onMouseDown: (event) => event.preventDefault() })}
          aria-label={options?.ariaLabel}
          type="button"
          height={height}
        >
          <Stack alignX="center">
            {toolbarButton}
            <DropdownIndicator />
          </Stack>
        </ToolbarButton>
      </Tooltip>
      {open ? (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 10000 }}>
            <Component
              attrs={{ ...getFloatingProps(), 'data-placement': placement }}
              instance={instance}
            />
          </div>
        </FloatingPortal>
      ) : null}
    </Portal>
  );
});
